/**
 * Utility functions for formatting numbers, currency, dates, and energy values
 */

export const formatNumber = (num?: number, decimals = 0) => {
  if (num == null) return '-';
  return num.toLocaleString('en-AU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatCurrency = (amount?: number, decimals?: number) => {
  if (amount == null) return '-';

  // If explicit decimals provided, use that
  if (decimals !== undefined) {
    return `$${amount.toLocaleString('en-AU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }

  const absAmount = Math.abs(amount);

  // For amounts < $1, show in cents (e.g., 12.5c)
  if (absAmount < 1 && absAmount > 0) {
    const cents = amount * 100;
    const centDecimals = absAmount < 0.1 ? 2 : 1; // 1.25c vs 12.5c
    return `${cents.toLocaleString('en-AU', {
      minimumFractionDigits: centDecimals,
      maximumFractionDigits: centDecimals,
    })}c`;
  }

  // For amounts < $100, show 2 decimals (e.g., $1.53, $15.60)
  if (absAmount < 100) {
    return `$${amount.toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // For amounts >= $100, show no decimals (e.g., $129, $1,234)
  return `$${amount.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export const formatDate = (date?: Date | string | null) => {
  if (!date) return '-';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '-';
  }
};

/** Full date and time in AEST/AEDT, for audit stamps that outlive the current year. */
export const formatDateTimeAu = (date?: Date | string | null) => {
  if (!date) return '-';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Australia/Sydney',
    });
  } catch {
    return '-';
  }
};

/** "31 Dec 2026", the style used for periods and stamps across the activity feed. */
export const formatDateShort = (date?: Date | string | null) => {
  if (!date) return '-';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

export const formatDateTime = (date?: Date | string | null) => {
  if (!date) return '-';
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleString('en-AU', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '-';
  }
};

export const formatEnergyWithUnit = (kwhValue?: number) => {
  if (kwhValue == null) return '-';

  const whValue = kwhValue * 1000; // Convert to Wh for scaling

  if (whValue < 1000) {
    return `${formatNumber(whValue, 0)} Wh`;
  } else if (whValue < 1000000) {
    return `${formatNumber(whValue / 1000, 1)} kWh`;
  } else if (whValue < 1000000000) {
    return `${formatNumber(whValue / 1000000, 2)} MWh`;
  } else {
    return `${formatNumber(whValue / 1000000000, 3)} GWh`;
  }
};

export const formatPercentage = (value?: number, decimals = 1) => {
  if (value == null) return '-';
  return `${formatNumber(value * 100, decimals)}%`;
};

export const formatRate = (rate?: number, unit = 'kWh') => {
  if (rate == null) return '-';
  return `${formatCurrency(rate, 3)}/${unit}`;
};

/**
 * Format ABN (Australian Business Number) with spaces
 * Format: XX XXX XXX XXX (11 digits)
 * Example: 12345678901 -> 12 345 678 901
 */
export const formatABN = (abn?: string) => {
  if (!abn) return '-';
  const digitsOnly = abn.replace(/\D/g, '');
  if (digitsOnly.length !== 11) return abn; // Return as-is if invalid length
  return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 5)} ${digitsOnly.slice(5, 8)} ${digitsOnly.slice(8, 11)}`;
};

/**
 * Format ACN (Australian Company Number) with spaces
 * Format: XXX XXX XXX (9 digits)
 * Example: 123456789 -> 123 456 789
 */
export const formatACN = (acn?: string) => {
  if (!acn) return '-';
  const digitsOnly = acn.replace(/\D/g, '');
  if (digitsOnly.length !== 9) return acn; // Return as-is if invalid length
  return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6, 9)}`;
};

/**
 * Format a period date range (e.g., for service periods)
 * Examples:
 * - Same month: "Dec 2024"
 * - Multi-month same year: "Nov - Dec 2024"
 * - Spans years: "Dec 2024 - Jan 2025"
 */
export const formatMonthYear = (dateStr?: string | Date): string => {
  if (!dateStr) return '-';
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  return date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};

export const formatPeriod = (periodStartDate?: string | Date, periodEndDate?: string | Date): string => {
  if (!periodStartDate || !periodEndDate) {
    return 'Never';
  }

  const start = new Date(periodStartDate);
  const end = new Date(periodEndDate);
  // UTC accessors: periods store end as end-of-month UTC (23:59:59.999), which
  // local-time reads push into the next month in AU and split a single month
  // into a spurious "Apr - May" range.
  const startMonth = start.toLocaleDateString('en-AU', { month: 'short', timeZone: 'UTC' });
  const endMonth = end.toLocaleDateString('en-AU', { month: 'short', timeZone: 'UTC' });
  const startYear = start.getUTCFullYear().toString();
  const endYear = end.getUTCFullYear().toString();

  // Check if it's a single month period
  if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
    return `${startMonth} ${startYear}`;
  }

  // Multi-month period
  if (start.getUTCFullYear() === end.getUTCFullYear()) {
    return `${startMonth} - ${endMonth} ${startYear}`;
  }

  // Spans across years
  return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
};
